/**
 * Authorities — the Company Admin decides which ranks hold which powers.
 * Route: /authorities
 * @file src/web/features/authorities/pages/index.tsx
 *
 * @llm-rule WHEN: The Company Admin delegates approval and remark powers downward
 * @llm-rule NOTE: The Company Admin column is fixed and always ticked — they are the one
 *   delegating, so they cannot lock themselves out. Employees never appear:
 *   these are supervisory powers by definition.
 * @llm-rule NOTE: TWO layouts, one state. Below `md` this is a list of cards
 *   with a full-width switch per rank; above `md` it is the comparison matrix.
 *   The matrix alone was `1fr + 3×88px` — on a 390px phone that left ~90px for
 *   a column holding a sentence, so every authority wrapped into an unreadable
 *   ribbon and the checkboxes were a 20px target at the screen edge.
 * @llm-rule AVOID: Declaring these sub-components inside the page function.
 *   A component defined during render is a new type every keystroke, so React
 *   unmounts and remounts it and the row loses focus mid-interaction.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Checkbox, Skeleton, toast } from '@bloomneo/uikit';
import { Check, Lock, ShieldCheck } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import {
  CAPABILITIES,
  CAPABILITY_HINT,
  CAPABILITY_LABEL,
  DELEGATABLE_ROLES,
  DEFAULT_CAPABILITIES,
  ROLE_LABEL,
  type Capability,
  type CapabilityMap,
  type OrgRole,
} from '../../../shared/domain';

/**
 * A switch sized for a thumb.
 *
 * The whole row is the target, not just the 20px control — reaching a checkbox
 * pinned to the right edge of a phone is the single worst thing about a matrix
 * rendered narrow.
 */
function RoleSwitch({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onToggle(!on)}
      className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors active:scale-[0.99]"
      style={{ background: on ? 'var(--state-good-bg)' : 'var(--paper-sunk)' }}
    >
      <span className={`text-[13.5px] font-medium ${on ? 'text-ink' : 'text-ink-faint'}`}>
        {label}
      </span>

      <span
        aria-hidden
        className="relative inline-flex h-[26px] w-[44px] flex-none items-center rounded-full transition-colors"
        style={{ background: on ? 'var(--brand)' : 'var(--rule)' }}
      >
        <span
          className="absolute size-[20px] rounded-full bg-white transition-all"
          style={{ left: on ? 21 : 3, boxShadow: 'var(--elev-1)' }}
        />
      </span>
    </button>
  );
}

/** One authority as a card — the mobile layout. */
function AuthorityCard({
  capability,
  held,
  onToggle,
}: {
  capability: Capability;
  held: OrgRole[];
  onToggle: (role: OrgRole, on: boolean) => void;
}) {
  return (
    <li className="rounded-[14px] border border-rule bg-paper p-4">
      <div className="text-[14px] font-medium leading-snug text-ink">
        {CAPABILITY_LABEL[capability]}
      </div>
      <p className="mt-1 text-[12px] leading-relaxed text-ink-faint">
        {CAPABILITY_HINT[capability]}
      </p>

      <div className="mt-3 flex flex-col gap-1.5">
        {/* Stated, not hidden: the admin holds this already and cannot drop it. */}
        <div
          className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5"
          style={{ background: 'var(--paper-sunk)' }}
        >
          <span className="text-[13.5px] font-medium text-ink-faint">
            {ROLE_LABEL.COMPANY_ADMIN}
          </span>
          <span className="flex items-center gap-1.5 text-[11.5px] text-ink-ghost">
            Always
            <Lock className="size-3.5" strokeWidth={2} />
          </span>
        </div>

        {DELEGATABLE_ROLES.map((role) => (
          <RoleSwitch
            key={role}
            label={ROLE_LABEL[role]}
            on={held.includes(role)}
            onToggle={(next) => onToggle(role, next)}
          />
        ))}
      </div>
    </li>
  );
}

function AuthoritiesPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();

  const [capabilities, setCapabilities] = useState<CapabilityMap>(DEFAULT_CAPABILITIES);
  const [saved, setSaved] = useState<CapabilityMap>(DEFAULT_CAPABILITIES);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const result = await request<{ capabilities: CapabilityMap }>('/api/org/capabilities');
    if (result.ok) {
      setCapabilities(result.data.capabilities);
      setSaved(result.data.capabilities);
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = JSON.stringify(capabilities) !== JSON.stringify(saved);

  function toggle(capability: Capability, role: OrgRole, on: boolean) {
    setCapabilities((prev) => {
      const held = new Set(prev[capability]);
      if (on) held.add(role);
      else held.delete(role);
      return { ...prev, [capability]: DELEGATABLE_ROLES.filter((r) => held.has(r)) };
    });
  }

  async function save() {
    setBusy(true);
    try {
      const result = await request<{ capabilities: CapabilityMap }>('/api/org/capabilities', {
        method: 'PUT',
        body: capabilities,
      });
      if (!result.ok) return toast.error(result.error);

      setSaved(result.data.capabilities);
      setCapabilities(result.data.capabilities);
      toast.success('Authorities updated');
    } finally {
      setBusy(false);
    }
  }

  if ((user?.orgRole ?? 'EMPLOYEE') !== 'COMPANY_ADMIN') {
    return <Navigate to="/clock" replace />;
  }

  const footnote = (
    <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-ink-ghost">
      <ShieldCheck className="mt-0.5 size-3.5 flex-none" />
      Rank still applies on top. Granting an authority never lets someone act on a peer or a
      superior, or outside their own team or department.
    </p>
  );

  return (
    <AppShell
      wide
      title="Authorities"
      subtitle="Choose which ranks can act on your behalf. Everything here is yours to grant or take back."
    >
      {loading ? (
        <Skeleton className="h-80 w-full rounded-[18px]" />
      ) : (
        <div className="mx-auto max-w-3xl">
          {/* ---------------------------------------------------------------
              PHONE — a card per authority, each rank a full-width switch.
              --------------------------------------------------------------- */}
          <ul className="flex flex-col gap-2.5 md:hidden">
            {CAPABILITIES.map((capability) => (
              <AuthorityCard
                key={capability}
                capability={capability}
                held={capabilities[capability]}
                onToggle={(role, on) => toggle(capability, role, on)}
              />
            ))}
          </ul>

          <div className="mt-3 md:hidden">{footnote}</div>

          {/* ---------------------------------------------------------------
              DESKTOP — the comparison matrix, where the width exists for it.
              --------------------------------------------------------------- */}
          <Panel padded={false} className="hidden overflow-hidden md:block">
            <div className="grid grid-cols-[1fr_repeat(3,88px)] items-center gap-2 border-b border-rule bg-paper-warm px-5 py-2.5">
              <SectionLabel>Authority</SectionLabel>
              {(['COMPANY_ADMIN', ...DELEGATABLE_ROLES] as OrgRole[]).map((role) => (
                <div
                  key={role}
                  className="text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-ghost"
                >
                  {ROLE_LABEL[role]}
                </div>
              ))}
            </div>

            <ul className="divide-y divide-rule">
              {CAPABILITIES.map((capability) => (
                <li
                  key={capability}
                  className="grid grid-cols-[1fr_repeat(3,88px)] items-center gap-2 px-5 py-4"
                >
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-medium text-ink">
                      {CAPABILITY_LABEL[capability]}
                    </div>
                    <p className="mt-0.5 text-[11.5px] leading-relaxed text-ink-faint">
                      {CAPABILITY_HINT[capability]}
                    </p>
                  </div>

                  {/* The admin is always on, and not editable. */}
                  <div
                    className="flex justify-center"
                    title="The Company Admin always holds every authority"
                  >
                    <span className="flex size-5 items-center justify-center rounded bg-appreciation/15">
                      <Check className="size-3.5 text-appreciation" />
                    </span>
                  </div>

                  {DELEGATABLE_ROLES.map((role) => (
                    <div key={role} className="flex justify-center">
                      <Checkbox
                        checked={capabilities[capability].includes(role)}
                        onCheckedChange={(checked) => toggle(capability, role, checked === true)}
                        aria-label={`${CAPABILITY_LABEL[capability]} — ${ROLE_LABEL[role]}`}
                      />
                    </div>
                  ))}
                </li>
              ))}
            </ul>

            <div className="border-t border-rule bg-paper-warm px-5 py-3">{footnote}</div>
          </Panel>

          {/* ---------------------------------------------------------------
              Save — sticky above the tab bar on a phone, so a change made at
              the bottom of a long list never needs a scroll back up to commit.
              --------------------------------------------------------------- */}
          <div
            className="sticky bottom-0 z-10 mt-4 flex items-center justify-end gap-2 border-t border-rule bg-paper-warm/95 py-3 backdrop-blur-md md:static md:border-0 md:bg-transparent md:py-0 md:backdrop-blur-none"
          >
            {dirty && (
              <Button variant="outline" onClick={() => setCapabilities(saved)} disabled={busy}>
                Discard
              </Button>
            )}
            <Button onClick={() => void save()} disabled={!dirty || busy}>
              {busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default function AuthoritiesRoute() {
  return (
    <AuthGuard>
      <AuthoritiesPage />
    </AuthGuard>
  );
}
